import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/SharedAuthContext';
import { OPEN_ASSISTANT_EVENT, type OpenAssistantDetail } from '@/lib/open-assistant';
import { SESSION_EXPIRED_EVENT } from '@/lib/session-expired';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { AssistantFAB } from '@/components/assistant/AssistantFAB';
import { AssistantChatDrawer } from '@/components/assistant/AssistantChatDrawer';
import { usePageContext } from '@/contexts/PageContext';
import { trpc } from '@/lib/trpc';
import { MfaEnforcementBanner } from '@/components/auth/MfaEnforcementBanner';
import { MfaRequiredScreen } from '@/components/auth/MfaRequiredScreen';

export function DashboardLayout() {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Colapsado del menú: se recuerda entre sesiones (es preferencia de la
  // persona, no estado de la pantalla).
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('internal.sidebar.collapsed') === '1'; } catch { return false; }
  });
  const toggleCollapsed = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem('internal.sidebar.collapsed', next ? '1' : '0'); } catch { /* modo privado */ }
      return next;
    });
  };
  const [initialQuestion, setInitialQuestion] = useState<string | undefined>();
  const { partnerCtx, taCtx, omlCtx } = usePageContext();
  // Modo del asistente: hub de un partner, Talent Acquisition o el general.
  // Una pregunta clickeada en la burbuja de un modo no debe heredarla el otro
  // (el drawer cambia de cuerpo al cambiar de sección con el drawer abierto).
  const assistantMode = partnerCtx?.partnerId ?? (taCtx ? 'ta' : omlCtx ? 'oml' : 'default');
  useEffect(() => { setInitialQuestion(undefined); }, [assistantMode]);

  const openWithQuestion = (q: string) => {
    setInitialQuestion(q);
    setIsChatOpen(true);
  };

  const handleClose = () => {
    setIsChatOpen(false);
    setInitialQuestion(undefined);
  };

  // El Home (botón "Abrir supportIA") y la subtab Asistente de TA abren el drawer
  // desde afuera del layout, opcionalmente con una pregunta cargada (lib/open-assistant.ts).
  useEffect(() => {
    const open = (e: Event) => {
      const question = (e as CustomEvent<OpenAssistantDetail | undefined>).detail?.question;
      if (question) openWithQuestion(question);
      else setIsChatOpen(true);
    };
    window.addEventListener(OPEN_ASSISTANT_EVENT, open);
    return () => window.removeEventListener(OPEN_ASSISTANT_EVENT, open);
  }, []);

  // MFA (Vault F2): 2SV de Google. Banner mientras no sea obligatoria; pantalla
  // de bloqueo cuando lo es y la persona no la tiene. `refresh` salta el cache
  // del backend (botón "Ya la activé"). Este path está exento del gate global.
  const [forceMfa, setForceMfa] = useState(false);
  const mfa = trpc.profile.getMfaStatus.useQuery({ refresh: forceMfa }, { staleTime: 10 * 60_000, retry: false });
  const recheckMfa = async () => {
    // La primera vez cambia el input (refresh:true) y React Query refetchea solo;
    // las siguientes, refetch explícito sobre esa misma key.
    if (forceMfa) await mfa.refetch();
    else setForceMfa(true);
  };
  const mfaBlocked = !!mfa.data && mfa.data.enforcedNow && mfa.data.enrolled === false;
  const mfaWarn = !!mfa.data && !mfaBlocked && mfa.data.enrolled === false;
  // Escape anti-lockout (review F2): un admin sin 2SV sigue pudiendo entrar a
  // /admin/internal (pestaña Seguridad) para correr o apagar el enforcement —
  // el backend exime esos paths, y acá el bloqueo no tapa esa ruta.
  const { user, logout } = useAuth();
  const location = useLocation();
  const isAdmin = !!user?.roles?.includes('admin');
  const adminEscape = isAdmin && location.pathname.startsWith('/admin/internal');

  // Sesión vencida (lib/session-expired.ts): un 401 de la API es UN aviso y
  // vuelta al login, no un error de cada pantalla ("la descarga de la base
  // falló" en TA, 28/09). Al cerrar sesión, ProtectedRoute redirige a /login.
  const { t } = useTranslation();
  useEffect(() => {
    const expired = () => {
      toast.error(t('auth.session_expired'));
      void logout();
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, expired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, expired);
  }, [logout, t]);

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        collapsed={sidebarCollapsed}
        onToggleCollapse={toggleCollapsed}
      />

      <div
        className={
          'flex flex-1 flex-col overflow-hidden pl-0 transition-[padding] duration-200 ' +
          (sidebarCollapsed ? 'md:pl-16' : 'md:pl-64')
        }
      >
        <Topbar onMenuClick={() => setSidebarOpen(true)} />

        {mfaWarn && (
          <MfaEnforcementBanner
            enforceFrom={mfa.data!.enforceFrom}
            daysUntil={mfa.data!.daysUntil}
            onRecheck={recheckMfa}
            rechecking={mfa.isFetching}
          />
        )}

        <main className="flex-1 overflow-y-auto bg-background p-3 sm:p-4 md:p-6">
          {mfaBlocked && !adminEscape ? (
            <MfaRequiredScreen
              variant="internal"
              enrolled={mfa.data!.enrolled}
              onRecheck={recheckMfa}
              rechecking={mfa.isFetching}
              adminEscapeHref={isAdmin ? '/admin/internal?tab=security' : undefined}
            />
          ) : (
            <Outlet />
          )}
        </main>
      </div>

      <AssistantFAB
        onClick={() => setIsChatOpen(true)}
        partnerCtx={partnerCtx}
        onPartnershipQuestion={openWithQuestion}
        taActive={!!taCtx}
        taScope={taCtx?.variant ?? 'metrics'}
        onTaQuestion={openWithQuestion}
        omlActive={!!omlCtx}
        onOmlQuestion={openWithQuestion}
      />

      <AssistantChatDrawer
        isOpen={isChatOpen}
        onClose={handleClose}
        partnerCtx={partnerCtx}
        taActive={!!taCtx}
        taScope={taCtx?.variant ?? 'metrics'}
        omlActive={!!omlCtx}
        initialQuestion={initialQuestion}
      />
    </div>
  );
}
